/**
 * Centralized theme configuration using Radix UI
 * This file contains all theme-related constants for consistent styling
 * across the application.
 * 
 * AI Agent Note: Update this file to modify colors, spacing, or typography globally.
 * Uses Radix UI Colors for semantic color system.
 */

import '@radix-ui/colors/violet-dark.css';
import '@radix-ui/colors/slate-dark.css';
import '@radix-ui/colors/red-dark.css';
import '@radix-ui/colors/amber-dark.css';
import '@radix-ui/colors/green-dark.css';
import '@radix-ui/colors/blue-dark.css';

/**
 * Radix UI Colors mapped to semantic names
 * Using dark theme variants for astrophotography context
 * 
 * Color scales: 1 (lightest) to 12 (darkest)
 * - Steps 1-2: Backgrounds
 * - Steps 3-5: Component backgrounds
 * - Steps 6-8: Borders and separators  
 * - Steps 9-12: Text and solid colors
 */
export const theme = {
  colors: {
    // Background colors (Slate dark scale)
    background: {
      primary: 'var(--slate-1)',      // App background
      secondary: 'var(--slate-2)',    // Subtle background
      tertiary: 'var(--slate-3)',     // Component background
      hover: 'var(--slate-4)',        // Hover background
    },
    
    // Text colors (Slate dark scale)
    text: {
      primary: 'var(--slate-12)',     // High contrast text
      secondary: 'var(--slate-11)',   // Low contrast text
      muted: 'var(--slate-10)',       // Disabled/subtle text
    },
    
    // Accent colors (Violet dark scale)
    accent: {
      primary: 'var(--violet-9)',     // Primary accent
      primaryHover: 'var(--violet-10)', // Hover state
      subtle: 'var(--violet-3)',      // Subtle accent background
      border: 'var(--violet-7)',      // Accent borders
      text: 'var(--violet-11)',       // Accent text
      solid: 'var(--violet-9)',       // Solid backgrounds
    },
    
    // Status colors (Semantic color scales)
    status: {
      error: 'var(--red-9)',          // Error state
      errorBg: 'var(--red-3)',        // Error background
      warning: 'var(--amber-9)',      // Warning state
      warningBg: 'var(--amber-3)',    // Warning background
      success: 'var(--green-9)',      // Success state
      successBg: 'var(--green-3)',    // Success background
      info: 'var(--blue-9)',          // Info state
      infoBg: 'var(--blue-3)',        // Info background
    },
    
    // Border colors (Slate dark scale)
    border: {
      default: 'var(--slate-6)',      // Default borders
      hover: 'var(--slate-7)',        // Hover borders
      focus: 'var(--violet-8)',       // Focus rings
      subtle: 'var(--slate-5)',       // Subtle dividers
    },
  },
  
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '20px',
    xxl: '24px',
    xxxl: '32px',
  },
  
  borderRadius: {
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px',
  },
  
  fontSize: {
    xs: '0.75rem',
    sm: '0.875rem',
    md: '0.95rem',
    base: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
    xxl: '1.5rem',
    xxxl: '1.8rem',
  },
  
  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  
  transitions: {
    fast: '0.1s',
    normal: '0.2s',
    slow: '0.3s',
  },
  
  shadows: {
    sm: '0 1px 2px rgba(0, 0, 0, 0.2)',
    md: '0 4px 6px rgba(0, 0, 0, 0.3)',
    lg: '0 10px 15px rgba(0, 0, 0, 0.4)',
  },
} as const;

/**
 * Radix UI Theme configuration
 * Appearance: dark theme for astrophotography context
 * Accent color: purple to match existing design
 * Font: Space Grotesk for modern, technical aesthetic
 */
export const radixThemeConfig = {
  appearance: 'dark' as const,
  accentColor: 'violet' as const,
  grayColor: 'slate' as const,
  radius: 'medium' as const,
  scaling: '100%' as const,
  // Note: panelBackground set to 'solid' for better contrast in dark mode
  panelBackground: 'solid' as const,
};
